import React, {useEffect, useState} from 'react';
import {useResizeObserver} from "../../hooks/useResizeObserver";
import {GridPaginationModel} from "@mui/x-data-grid-pro";
import _ from "lodash";
import {ListFilter, addToUrlParams} from "@dreebit-registry/vsm-microservice-components";
import GridViewTable from "../../components/GridViewTable";
import {useSearchParams} from "react-router-dom";
import {Button} from "@mui/material";
import {Tooltip} from "@mui/joy";
import {useQuery} from "@apollo/client";
import {AddRounded} from "@mui-symbols-material/w300";
import {GridViewListTDAs} from "../../graphql/queries/gridViewListTDAs";
import {useTranslation} from "react-i18next";
import CreateGridViewModal from "../../components/GridViewTable/createGridViewModal";
import UpdateGridViewModal from "../../components/GridViewTable/updateGridViewModal";
import {ClientListTotal} from "../../graphql/queries/clientListTotal";

interface Props {
    onOpenGridView: (gridViewId: string) => void;
}


const GridViewListScreen = ({onOpenGridView}: Props) => {
    const {t} = useTranslation();
    const [ref, filterHeight] = useResizeObserver();
    const [viewportHeight, setViewportHeight] = useState(window.innerHeight);

    const [createGridViewModalOpen, setCreateGridViewModalOpen] = useState(false);
    const [editGridViewId, setEditGridViewId] = useState<string | null>(null);

    // A grid view always belongs to a company — without any visible company the
    // create modal's company picker would be empty. Errors keep the button enabled.
    const clientListTotalQuery = useQuery(ClientListTotal);
    const noClients = !clientListTotalQuery.loading
        && !clientListTotalQuery.error
        && _.get(clientListTotalQuery, 'data.clientList.total') === 0;

    const showEnvBanner = process.env.REACT_APP_SHOW_TESTING_BANNER === "true"
    const tableHeight =
        viewportHeight - filterHeight
        - (showEnvBanner ? 40 : 0) // Test Env Banner
        - 63 // header
        - 26; // content padding

    const filterKeys: string[] = ['clientId']

    const [urlParams, setUrlParams] = useSearchParams();

    const [searchParams, setSearchParams] = useState({
        query: urlParams.get('query') || '',
        fields: ['name']
    })

    const onDebouncedSearch = _.debounce((values) => {
        setSearchParams(values);
        addToUrlParams(urlParams, values, setUrlParams);
    }, 700);

    useEffect(() => {
        const handleResize = () => {
            setViewportHeight(window.innerHeight);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return (
        <>
            <div
                ref={ref}
                className={'flex-row'}
                style={{justifyContent: 'space-between', alignItems: 'center', padding: '0 8px 8px 0',}}
            >
                <ListFilter
                    query={GridViewListTDAs}
                    queryVariables={{typeDefinitionIndex: "GRID_VIEW_LIST_FILTER"}}
                    attributesPath={'gridViewList.typeDefinitionAttributeList.typeDefinitionAttributes'}
                    onSearch={onDebouncedSearch}
                    searchParams={searchParams}
                    filterKeys={filterKeys}
                    style={{paddingTop: '0px'}}
                    urlParams={urlParams}
                    setUrlParams={setUrlParams}
                />
                <Tooltip title={noClients ? t("You don't have access to any company, so no grid view can be created.") : ""}>
                    {/* span keeps the tooltip working while the button is disabled */}
                    <span>
                        <Button
                            variant="contained"
                            startIcon={<AddRounded/>}
                            disabled={noClients}
                            onClick={() => setCreateGridViewModalOpen(true)}
                        >
                            {t('Add')}
                        </Button>
                    </span>
                </Tooltip>
            </div>
            <GridViewTable
                height={tableHeight}
                searchParams={searchParams}
                urlParams={urlParams}
                filterKeys={filterKeys}
                onChangePagination={(newPaginationModel: GridPaginationModel) => {
                    if (newPaginationModel) {
                        addToUrlParams(urlParams, newPaginationModel, setUrlParams)
                    }
                }}
                onOpenGridView={onOpenGridView}
                onEditGridView={(id) => setEditGridViewId(id)}
            />
            {createGridViewModalOpen && (
                <CreateGridViewModal
                    open={createGridViewModalOpen}
                    onClose={() => setCreateGridViewModalOpen(false)}
                />
            )}
            {editGridViewId && (
                <UpdateGridViewModal
                    gridViewId={editGridViewId}
                    open={!!editGridViewId}
                    onClose={() => setEditGridViewId(null)}
                />
            )}
        </>
    );
};

export default GridViewListScreen;


